import { useEffect, useState } from 'react';
import { HorizontalLayout, VerticalLayout } from '../../common/common-layout'
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from '../MainLayout';
import { observer } from 'mobx-react-lite';
import { TitleLargeMedium, TitleNormal } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import theme from '../../common/theme';
import BTButton from '../../common/components/BTButton';
import { BTCheckBox } from '../../common/components/BTCheckBox';
import { useNavigate } from 'react-router-dom';
import DialogIconInfo from '../../assets/icons/dialog_icon_info.svg';
import { dialogStore } from '../../store/DialogStore';
import { AccountAddFormValues, accountAddSchema } from '../../schema/homepage/AccountAddSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { checkDuplicatedUsername, registerEmailUser, requestAuthenticationEmail, validateAuthCode } from '../../service/AccountService';
import { Button, TextField, CircularProgress } from '@mui/material';

const AccountAdd = () => {
    return (
        <MainLayout>
            <AccountAddBody />
        </MainLayout>
    );
};

export default AccountAdd;

const AccountAddBody = observer(() => {
    const navigate = useNavigate();
    const [isDuplecateUsername, setIsDuplicatedUsername] = useState<boolean | null>(null);
    const [error, setError] = useState("");
    const [isCheckingAuthCode, setIsCheckingAuthCode] = useState<boolean | null>(null);
    const [countDown, setCountDown] = useState<number>(0);
    const [isAuthCheckDone, setIsAuthCheckDone] = useState<boolean>(false);
    const [isSending, setIsSending] = useState(false);
    
    // 버튼 비활성화 상태 관리 (성공 시에만 제어)
    const isSendDisabled = countDown > 0 || isAuthCheckDone;
    const isInputsLocked = countDown > 0;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        watch
    } = useForm<AccountAddFormValues>({
        resolver: zodResolver(accountAddSchema),
    });

    const onCheckDuplicatedId = async () => {
        const username = watch("username");

        if (username === null || username === "") {
            dialogStore.showDialog({
                title: '알림',
                message: 'Username이 공백입니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (username.length < 4) {
            dialogStore.showDialog({
                title: '알림',
                message: '아이디는 최소 4자 이상이어야 합니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else {
            const isDuplicate = await checkDuplicatedUsername(username);
            setIsDuplicatedUsername(isDuplicate);

            if (isDuplicate) {
                dialogStore.showDialog({
                    title: '알림',
                    message: '아이디가 중복입니다.',
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            } else {
                dialogStore.showDialog({
                    title: '알림',
                    message: '해당 아이디는 사용이 가능합니다.',
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            }
        }
    };

    const onCheckAuthCode = async () => {
        const authCode = watch("authCode");

        if (authCode && authCode !== "" && countDown > 0) {
            const email = watch("email");
            const isAuthCodeChecked = await validateAuthCode(email, authCode);

            console.log("** isAuthCodeChecked: ", isAuthCodeChecked);

            if (!isAuthCodeChecked) {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일 인증번호가 일치하지 않습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            } else {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일 인증이 확인되었습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                        // 인증 완료 처리
                        setIsCheckingAuthCode(false);
                        setIsAuthCheckDone(true);
                    },
                    onClose: () => {
                        console.log('닫기');
                        // 인증 완료 처리
                        setIsCheckingAuthCode(false);
                        setIsAuthCheckDone(true);
                    },
                });
                return;
            }

        } else if (countDown <= 0) {
            dialogStore.showDialog({
                title: '알림',
                message: '인증 요청이 만료되었습니다. 재인증을 시도해 주세요.',
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else {
            dialogStore.showDialog({
                title: '알림',
                message: '인증번호가 공백입니다. 인증번호를 입력해주세요.',
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        }
    };

    const handleAccountAdd = async () => {
        const username = watch('username');
        const password = watch('password');
        const confirmPassword = watch('confirmPassword');
        const email = watch('email');
        const isSuperUser = watch('isSuperUser');
        const currentDatetime = new Date();

        if (!password) {
            return;
        }

        try {
            // 이메일 인증 검증
            if (!email || email === "") {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일을 입력해주세요.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            }

            if (!isAuthCheckDone) {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일 인증이 완료되지 않았습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            }

            if (isDuplecateUsername === null) {
                dialogStore.showDialog({
                    title: '알림',
                    message: 'Username 중복 체크를 진행해주세요.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            } else if (isDuplecateUsername) {
                dialogStore.showDialog({
                    title: '알림',
                    message: 'Username이 중복입니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            } else if (password !== confirmPassword) {
                dialogStore.showDialog({
                    title: '알림',
                    message: 'Password가 일치하지 않습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
                return;
            }

            // TODO: createdBy 로직 수정
            await registerEmailUser({
                id: "",
                username: username,
                email: email,
                isWithdrawal: false,
                isSuperUser: isSuperUser || false,
                createdBy: "system",
                createdAt: currentDatetime,
            }, password);

            dialogStore.showDialog({
                title: '알림',
                message: `관리자 계정(${username}) 생성이 완료되었습니다`,
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                    navigate("/account");
                },
                onClose: () => {
                    console.log('닫기');
                    navigate("/account");
                },
            });
        } catch (error: unknown) {
            // 에러 메시지가 있는 경우 해당 메시지 사용, 없으면 기본 메시지 사용
            const errorMessage = error instanceof Error ? error.message : `관리자 계정(${username}) 생성에 실패하였습니다.`;
            
            dialogStore.showDialog({
                title: '주의',
                message: errorMessage,
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
        }
    };

    const onRequestAuthenticationEmail = async () => {
        const username = watch("username");
        const email = watch("email");

        if (isSendDisabled) return;

        if (username === null || username === "") {
            dialogStore.showDialog({
                title: '알림',
                message: '아이디가 공백입니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (email === null || email === "") {
            dialogStore.showDialog({
                title: '알림',
                message: '이메일이 공백입니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else {
            try {
                setIsSending(true);
                const result = await requestAuthenticationEmail(email, username, false);

                if (result) {
                    // 성공 시에만 버튼 제어 상태 변경
                    setIsCheckingAuthCode(true);
                    setIsAuthCheckDone(false);
                    setCountDown(180);
                    setValue('authCode', '');
                } else {
                    dialogStore.showDialog({
                        title: '알림',
                        message: '아이디 및 이메일에 해당하는 계정 정보가 존재하지 않습니다.',
                        icon: <img src={DialogIconInfo} />,
                        onConfirm: () => {
                            console.log('확인');
                        },
                        onClose: () => {
                            console.log('닫기');
                        },
                    });
                }
            } catch {
                dialogStore.showDialog({
                    title: '알림',
                    message: '인증 이메일 전송 요청이 실패하였습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
            } finally {
                setIsSending(false);
            }
        }
    };

    useEffect(() => {
        if (countDown <= 0) return;

        const timer = setInterval(() => {
            setCountDown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer); // cleanup
    }, [countDown]);

    const formatTime = (sec: number): string => {
        const minutes = Math.floor(sec / 60);
        const seconds = sec % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleCancel = (): void => {
        navigate(-1);
    };

    return (
        <div style={{ width: '100%', minHeight: `calc(100vh - ${MAIN_RIGHT_TOP_HEIGHT})`, display: 'flex', flexDirection: 'column' }} >
            <VerticalLayout height={'auto'} paddingLeft={36} paddingRight={36}>
                <form style={{ width: 516 }}>
                    <TitleLargeMedium>관리자 계정 추가</TitleLargeMedium>
                    <BTBox height='36px' />
                    <div style={{ width: '678px'}}>
                        <TitleNormal color='#8a92a6'>아이디</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                disabled={isInputsLocked}
                                {...register("username")}
                                error={!!errors.username}
                                helperText={errors.username?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.username ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                            <BTBox width="12px" />
                            <Button 
                                disabled={isInputsLocked}
                                style={{
                                    height: '56px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    backgroundColor: isInputsLocked ? '#ccc' : theme.colors.bbTech, // 원하는 색상
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    whiteSpace: 'nowrap',    // ✅ 줄바꿈 방지
                                    padding: '0 16px',       // ✅ 좌우 여유 공간
                                    width: 'auto',           // ✅ 내용에 따라 버튼 크기 조절
                                    minWidth: 'fit-content', // ✅ 너무 좁게 줄어들지 않도록
                                }} 
                                onClick={onCheckDuplicatedId}
                            >
                                아이디 중복체크
                            </Button>
                        </HorizontalLayout>
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>비밀번호</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                {...register("password")}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.username ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                        </HorizontalLayout>
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>비밀번호 확인</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                {...register("confirmPassword")}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.username ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                        </HorizontalLayout>
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>이메일</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                disabled={isInputsLocked}
                                {...register("email")}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.email ? '#c03221' : (isAuthCheckDone ? '#ccc' : theme.colors.bbTech),
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isAuthCheckDone ? '#ccc' : theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isAuthCheckDone ? '#ccc' : theme.colors.bbTech,
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'transparent',
                                            '& fieldset': {
                                                borderColor: isAuthCheckDone ? '#ccc' : theme.colors.bbTech,
                                            },
                                        },
                                    },
                                }}
                            />
                            <BTBox width="12px" />
                            <Button 
                                disabled={isSendDisabled || isSending}
                                style={{
                                    height: '56px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    backgroundColor: (isSendDisabled || isSending) ? '#ccc' : theme.colors.bbTech, // 원하는 색상
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    whiteSpace: 'nowrap',
                                    padding: '0 16px',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    gap: 8,
                                }} 
                                onClick={onRequestAuthenticationEmail}
                            >
                                <span style={{ visibility: isSending ? 'hidden' : 'visible' }}>
                                    인증번호 전송
                                </span>

                                {/* 로딩 스피너 (버튼 중앙 오버레이) */}
                                {isSending && (
                                    <CircularProgress
                                        size={22}
                                        sx={{
                                            color: 'white',
                                            position: 'absolute',
                                            inset: 0,
                                            margin: 'auto',
                                        }}
                                    />
                                )}
                            </Button>
                        </HorizontalLayout>
                        {
                            isCheckingAuthCode ?
                                <VerticalLayout>
                                    <BTBox height='16px' />
                                    <TitleNormal color='#8a92a6'>인증번호</TitleNormal>
                                    <BTBox height='8px' />
                                    <HorizontalLayout>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            {...register("authCode")}
                                            error={!!errors.authCode}
                                            helperText={errors.authCode?.message}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: errors.authCode ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.colors.bbTech,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.colors.bbTech,
                                                    },
                                                },
                                            }}
                                        />
                                        <BTBox width="6px" />
                                        <div
                                            style={{
                                                marginLeft: '10px',
                                                marginRight: '10px',
                                                display: 'flex',
                                                alignItems: 'center', // ✅ 수직 센터 정렬
                                                justifyContent: 'center', // (선택) 가로도 정렬
                                                height: '56px', // TextField, Button 높이에 맞춤
                                            }}
                                        >
                                            <TitleNormal color="#8a92a6">
                                                {formatTime(countDown)}
                                            </TitleNormal>
                                        </div>
                                        <BTBox width="6px" />
                                        <Button style={{
                                            height: '56px',
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            backgroundColor: '#E9ECEF',
                                            color: theme.colors.textCancel,
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            whiteSpace: 'nowrap',
                                            padding: '0 16px',
                                            width: 'auto',
                                            minWidth: 'fit-content',
                                        }} onClick={onCheckAuthCode}>인증번호 확인</Button>
                                    </HorizontalLayout>
                                </VerticalLayout>
                                : <div />
                        }
                        <BTBox height='16px' />
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                flexWrap: "nowrap",            // 줄바꿈 방지
                                fontSize: "14px",
                                color: "#6C757D",
                            }}
                        >
                            <TitleNormal color='#8a92a6'>슈퍼관리자 여부</TitleNormal>
                            <BTCheckBox
                                checked={watch('isSuperUser') || false}
                                onChange={(e) => setValue('isSuperUser', e.target.checked)}
                            />
                        </div>
                        <BTBox height='16px' />

                        {error && <p style={{ color: "red" }}>{error}</p>}
                    </div>
                </form>
            </VerticalLayout>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingRight: '76px', paddingBottom: '76px' }}>
                <HorizontalLayout style={{ marginTop: 'auto', justifyContent: 'flex-end' }}>
                    <BTButton 
                        color={!isAuthCheckDone ? '#ccc' : theme.colors.bbTech} 
                        textColor='white' 
                        disabled={!isAuthCheckDone}
                        onClick={handleAccountAdd}
                    >
                        저장
                    </BTButton>
                    <BTBox width='24px' />
                    <BTButton onClick={handleCancel}>취소</BTButton>
                </HorizontalLayout>
            </div>
        </div>
    );
});